import { ArrowLeft, Download, FileText } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRevenueBookCustom, getRevenueBookExportPdf, getRevenueBookExportExcel } from '../../../api/report.api';
import { getShop } from '../../../api/shop.api';
import styles from '../../../assets/scss/pages/RevenueBook.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../stores/authStore';

function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getQuarterRange() {
    const now = new Date();
    const qStart = Math.floor(now.getMonth() / 3) * 3;
    return {
        startDate: fmtDate(new Date(now.getFullYear(), qStart, 1)),
        endDate: fmtDate(new Date(now.getFullYear(), qStart + 3, 0)),
    };
}

function formatPeriodLabel(s, e) {
    const fmt = (d) => { const p = d.split('-'); return `${p[2]}/${p[1]}/${p[0]}`; };
    return `${fmt(s)} - ${fmt(e)}`;
}

function formatNum(v) {
    return (v || 0).toLocaleString('vi-VN');
}

function downloadBlob(data, filename, mime) {
    const blob = new Blob([data], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

export default function RevenueBookS1a() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const user = useAuthStore((s) => s.user);
    const canView = hasPermission(PERMISSIONS.REVENUE_BOOK.VIEW);
    const canExport = hasPermission(PERMISSIONS.REVENUE_BOOK.EXPORT);

    const initRange = getQuarterRange();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState('');
    const [startDate, setStartDate] = useState(initRange.startDate);
    const [endDate, setEndDate] = useState(initRange.endDate);
    const [bookData, setBookData] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);

    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1 });
    const PAGE_SIZE = 50;

    // Form fields
    const [taxCode, setTaxCode] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [businessAddress, setBusinessAddress] = useState('');
    const [businessLocation, setBusinessLocation] = useState('');
    const [periodLabel, setPeriodLabel] = useState('');

    // Fetch shop info once
    useEffect(() => {
        const fetchShop = async () => {
            if (!user?.shopId) return;
            try {
                const { data } = await getShop(user.shopId);
                const shop = data.shop || data;
                setBusinessName(shop.nameShop || '');
                setBusinessAddress(shop.address || '');
                setBusinessLocation(shop.address || '');
            } catch { /* ignore */ }
        };
        fetchShop();
    }, [user?.shopId]);

    // Update period label when dates change
    useEffect(() => {
        setPeriodLabel(formatPeriodLabel(startDate, endDate));
    }, [startDate, endDate]);

    // Fetch book data (server-side pagination)
    const fetchData = useCallback(async (p = page) => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await getRevenueBookCustom({ startDate, endDate, page: p, limit: PAGE_SIZE });

            // API trả { revenueBookData: [], pagination: {} } khi phân trang, hoặc array khi không
            const items = Array.isArray(data) ? data : (data?.revenueBookData || []);
            setBookData(items);

            if (data?.pagination) {
                setPagination(data.pagination);
            } else {
                setPagination({ totalItems: items.length, totalPages: 1 });
            }

            // Tổng: fetch 1 lần không phân trang khi page 1
            if (p === 1) {
                const { data: allData } = await getRevenueBookCustom({ startDate, endDate });
                const allItems = Array.isArray(allData) ? allData : (allData?.revenueBookData || []);
                setTotalRevenue(allItems.reduce((s, it) => s + (it.revenue || 0), 0));
            }
        } catch (err) {
            console.error('Error fetching revenue book:', err);
            setBookData([]);
            setTotalRevenue(0);
        } finally {
            setLoading(false);
        }
    }, [canView, startDate, endDate, page]);

    useEffect(() => { fetchData(page); }, [fetchData, page]);

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
        setPage(1);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
        setPage(1);
    };

    const exportParams = { startDate, endDate, taxCode, businessName, businessAddress, businessLocation, periodLabel };

    const handleExportPdf = async () => {
        if (!canExport || exporting) return;
        setExporting('pdf');
        try {
            const res = await getRevenueBookExportPdf(exportParams);
            downloadBlob(res.data, `SoDoanhThu_S1a-HKD_${startDate}_${endDate}.pdf`, 'application/pdf');
        } catch { alert('Xuất PDF thất bại'); }
        finally { setExporting(''); }
    };

    const handleExportExcel = async () => {
        if (!canExport || exporting) return;
        setExporting('excel');
        try {
            const res = await getRevenueBookExportExcel(exportParams);
            downloadBlob(res.data, `SoDoanhThu_S1a-HKD_${startDate}_${endDate}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        } catch { alert('Xuất Excel thất bại'); }
        finally { setExporting(''); }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <div>
                    <h1 className={styles.title}>Sổ doanh thu S1a-HKD</h1>
                    <p className={styles.subtitle}>Theo TT 152/2025/TT-BTC — Hộ kinh doanh doanh thu dưới 500 triệu</p>
                </div>
            </div>

            {/* Date + Export */}
            <div className={styles.filters}>
                <div className={styles.filterRow}>
                    <div className={styles.dateGroup}>
                        <span className={styles.dateLabel}>Từ ngày:</span>
                        <input type="date" className={styles.dateInput} value={startDate} onChange={handleStartDateChange} />
                    </div>
                    <div className={styles.dateGroup}>
                        <span className={styles.dateLabel}>Đến ngày:</span>
                        <input type="date" className={styles.dateInput} value={endDate} onChange={handleEndDateChange} />
                    </div>
                    {canExport && (
                        <div className={styles.exportBtns}>
                            <button className={`${styles.exportBtn} ${styles.exportBtnGreen}`} onClick={handleExportExcel} disabled={!!exporting}>
                                <Download /> {exporting === 'excel' ? 'Đang xuất...' : 'Excel'}
                            </button>
                            <button className={`${styles.exportBtn} ${styles.exportBtnBlue}`} onClick={handleExportPdf} disabled={!!exporting}>
                                <FileText /> {exporting === 'pdf' ? 'Đang xuất...' : 'PDF'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Form fields */}
            <div className={styles.formCard}>
                <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Tên hộ kinh doanh</label>
                        <input className={styles.formInput} value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Tên cửa hàng" />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Mã số thuế</label>
                        <input className={styles.formInput} value={taxCode} onChange={(e) => setTaxCode(e.target.value)} placeholder="Nhập mã số thuế" inputMode="numeric" />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Địa chỉ</label>
                        <input className={styles.formInput} value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} placeholder="Địa chỉ kinh doanh" />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Địa điểm kinh doanh</label>
                        <input className={styles.formInput} value={businessLocation} onChange={(e) => setBusinessLocation(e.target.value)} placeholder="Địa điểm" />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Kỳ kê khai</label>
                        <input className={styles.formInput} value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="VD: 01/04/2026 - 30/06/2026" />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableCard}>
                <div className={styles.bookHeader}>
                    <div className={styles.bookFormName}>Mẫu số S1a-HKD</div>
                    <div className={styles.bookTitle}>SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ</div>
                    <div className={styles.bookSubtitle}>
                        {businessName && `${businessName} — `}{periodLabel}
                    </div>
                </div>

                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải dữ liệu...
                    </div>
                ) : bookData.length === 0 ? (
                    <div className={styles.empty}>Không có dữ liệu trong khoảng thời gian này</div>
                ) : (
                    <>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 120 }}>Ngày tháng (A)</th>
                                <th>Diễn giải (B)</th>
                                <th style={{ width: 160 }}>Số tiền (1)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookData.map((item, idx) => {
                                const showDate = idx === 0 || item.date !== bookData[idx - 1]?.date;
                                return (
                                    <tr key={idx}>
                                        <td className={styles.dateCell}>{showDate ? item.date : ''}</td>
                                        <td>{item.description}</td>
                                        <td>{formatNum(item.revenue)} đ</td>
                                    </tr>
                                );
                            })}
                            {page >= pagination.totalPages && (
                                <tr className={styles.totalRow}>
                                    <td colSpan={2}>Tổng cộng</td>
                                    <td>{formatNum(totalRevenue)} đ</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <span className={styles.paginationInfo}>
                                {`${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, pagination.totalItems)} / ${pagination.totalItems}`}
                            </span>
                            <div className={styles.paginationControls}>
                                <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>{'‹'}</button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                    .filter((n) => n === 1 || n === pagination.totalPages || Math.abs(n - page) <= 2)
                                    .reduce((acc, n, i, arr) => {
                                        if (i > 0 && n - arr[i - 1] > 1) acc.push('dots-' + i);
                                        acc.push(n);
                                        return acc;
                                    }, [])
                                    .map((n) =>
                                        typeof n === 'string' ? (
                                            <span key={n} className={styles.pageDots}>...</span>
                                        ) : (
                                            <button key={n} className={`${styles.pageBtn}${n === page ? ` ${styles.pageBtnActive}` : ''}`} onClick={() => setPage(n)}>{n}</button>
                                        ),
                                    )}
                                <button className={styles.pageBtn} disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>{'›'}</button>
                            </div>
                        </div>
                    )}
                    </>
                )}
            </div>
        </div>
    );
}
